// Report tab — the on-screen quarterly report: an AI-written executive
// summary (cached server-side per project + period), the signal ledger, and
// the CSV statement download. The heavyweight PDF "statement" modal still
// exists from the header; this tab is the always-there, readable version.

import React, { useEffect, useState } from 'react';
import { blockedForTrial } from '../TrialUpgrade';
import {
  MonitoringReportData,
  formatPeriodRange,
} from './reportData';

interface MonitoringReportTabProps {
  projectId: number;
  data: MonitoringReportData | null;
  loading: boolean;
  from: string;
  to: string;
  onOpenStatement: () => void; // opens the existing PDF/CSV statement modal
}

const PRIORITY_TONES: Record<string, string> = {
  critical: 'bg-red-100 text-red-700',
  high: 'bg-orange-100 text-orange-700',
  medium: 'bg-yellow-100 text-yellow-700',
  low: 'bg-gray-100 text-gray-600',
};

const MonitoringReportTab: React.FC<MonitoringReportTabProps> = ({
  projectId, data, loading, from, to, onOpenStatement,
}) => {
  const [summary, setSummary] = useState<string | null>(null);
  const [summaryMeta, setSummaryMeta] = useState<{ cached: boolean; generatedAt: string } | null>(null);
  const [generating, setGenerating] = useState(false);
  const [summaryError, setSummaryError] = useState<string | null>(null);
  const [downloadingCsv, setDownloadingCsv] = useState(false);

  // Period changed → the summary on screen no longer describes it.
  useEffect(() => {
    setSummary(null);
    setSummaryMeta(null);
    setSummaryError(null);
  }, [from, to]);

  const generateSummary = async (force: boolean) => {
    setGenerating(true);
    setSummaryError(null);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`/api/monitoring/projects/${projectId}/report-summary`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ from, to, force }),
      });
      const body = await res.json();
      if (!res.ok) throw new Error(body?.error || 'Failed to generate summary');
      setSummary(body.summary);
      setSummaryMeta({ cached: !!body.cached, generatedAt: body.generatedAt });
    } catch (e: any) {
      setSummaryError(e?.message || 'Failed to generate summary');
    } finally {
      setGenerating(false);
    }
  };

  const downloadCSV = async () => {
    if (blockedForTrial()) return;
    setDownloadingCsv(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(
        `/api/monitoring/projects/${projectId}/report?from=${from}&to=${to}&format=csv`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!res.ok) throw new Error(`Download failed (${res.status})`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `monitoring-report-${from}_to_${to}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      // non-fatal; the statement modal offers the same download
    } finally {
      setDownloadingCsv(false);
    }
  };

  if (loading && !data) {
    return <div className="card p-6"><p className="text-gray-600 animate-fade-in">Preparing report…</p></div>;
  }
  if (!data) {
    return <div className="card p-6"><p className="text-gray-600">Could not load the report for this period.</p></div>;
  }

  const { summary: stats, signals } = data;

  return (
    <div className="space-y-4">
      {/* Report header */}
      <div className="card p-6">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <p className="section-label mb-1">Market monitoring report</p>
            <h3 className="text-xl font-bold tracking-tight text-gray-900">{data.project.name}</h3>
            <p className="text-sm text-gray-500 mt-0.5">{formatPeriodRange(data.period)}</p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={downloadCSV} disabled={downloadingCsv} className="btn-secondary text-sm">
              {downloadingCsv ? 'Preparing…' : '⬇ Download CSV'}
            </button>
            <button onClick={onOpenStatement} className="btn-secondary text-sm" title="Full statement with PDF export and type filters">
              📄 Full statement
            </button>
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-5">
          <div className="rounded-xl bg-gray-50 p-3">
            <p className="text-xs text-gray-500">Signals</p>
            <p className="text-2xl font-bold tracking-tight text-gray-900">{stats.total}</p>
          </div>
          <div className="rounded-xl bg-gray-50 p-3">
            <p className="text-xs text-gray-500">Market moves</p>
            <p className="text-2xl font-bold tracking-tight text-gray-900">{stats.totalMovements}</p>
          </div>
          <div className="rounded-xl bg-gray-50 p-3">
            <p className="text-xs text-gray-500">Companies gaining</p>
            <p className="text-2xl font-bold tracking-tight text-green-600">{stats.companiesGaining}</p>
          </div>
          <div className="rounded-xl bg-gray-50 p-3">
            <p className="text-xs text-gray-500">Companies losing</p>
            <p className="text-2xl font-bold tracking-tight text-red-600">{stats.companiesLosing}</p>
          </div>
        </div>
      </div>

      {/* Executive summary */}
      <div className="card p-6">
        <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
          <h3 className="text-lg font-semibold tracking-tight text-gray-900">✨ Executive summary</h3>
          <div className="flex items-center gap-2">
            {summary && (
              <button onClick={() => generateSummary(true)} disabled={generating} className="btn-ghost text-xs">
                🔄 Regenerate
              </button>
            )}
            {!summary && (
              <button
                onClick={() => generateSummary(false)}
                disabled={generating}
                className="px-4 py-2 bg-gradient-to-r from-violet-600 to-purple-600 text-white rounded-lg text-sm font-medium transition-all duration-200 hover:-translate-y-px hover:shadow-md disabled:opacity-60"
              >
                {generating ? 'Writing…' : 'Generate executive summary'}
              </button>
            )}
          </div>
        </div>

        {generating && !summary && (
          <p className="text-sm text-gray-500 animate-pulse">
            Reading this period's judged signals and movements, and writing the narrative…
          </p>
        )}
        {summaryError && (
          <p className="text-sm text-red-600 bg-red-50 rounded-lg p-3">{summaryError}</p>
        )}
        {summary ? (
          <div>
            <div className="text-[15px] leading-relaxed text-gray-800 space-y-3">
              {summary.split(/\n{2,}/).map((p, i) => <p key={i}>{p}</p>)}
            </div>
            {summaryMeta && (
              <p className="text-xs text-gray-400 mt-3">
                {summaryMeta.cached ? 'From cache' : 'Freshly generated'} ·{' '}
                {new Date(summaryMeta.generatedAt).toLocaleString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
              </p>
            )}
          </div>
        ) : !generating && !summaryError && (
          <p className="text-sm text-gray-500">
            A recruiter-grade narrative of the period — where talent flowed, genuine promotions versus
            profile activity, and patterns worth acting on. Written from this period's data, cached so
            your team sees the same report.
          </p>
        )}
      </div>

      {/* Signal ledger */}
      <div className="card p-6">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-lg font-semibold tracking-tight text-gray-900">Signal ledger</h3>
          <span className="text-sm text-gray-500">{signals.length} signal{signals.length === 1 ? '' : 's'}</span>
        </div>
        {signals.length === 0 ? (
          <p className="text-sm text-gray-500 py-6 text-center">No signals recorded in this period.</p>
        ) : (
          <div className="overflow-x-auto max-h-[32rem] overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-white">
                <tr className="text-left text-xs text-gray-500 border-b border-gray-200">
                  <th className="py-2 pr-3 font-medium">Date</th>
                  <th className="py-2 pr-3 font-medium">Candidate</th>
                  <th className="py-2 pr-3 font-medium">Signal</th>
                  <th className="py-2 pr-3 font-medium">Judged as</th>
                  <th className="py-2 font-medium">Priority</th>
                </tr>
              </thead>
              <tbody>
                {signals.map(s => (
                  <tr key={s.id} className="border-b border-gray-100 last:border-0 hover:bg-gray-50 transition-colors align-top">
                    <td className="py-2.5 pr-3 whitespace-nowrap text-gray-500">
                      {new Date(s.date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
                    </td>
                    <td className="py-2.5 pr-3">
                      <span className="font-medium text-gray-900">{s.candidate_name || '—'}</span>
                      {s.candidate_company && <span className="block text-xs text-gray-500">{s.candidate_company}</span>}
                    </td>
                    <td className="py-2.5 pr-3">
                      <span className="text-gray-800">{s.type_label}</span>
                      {s.title && <span className="block text-xs text-gray-500 max-w-xs truncate" title={s.title}>{s.title}</span>}
                    </td>
                    <td className="py-2.5 pr-3">
                      {s.ai_category_label ? (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-violet-50 text-violet-700 capitalize">{s.ai_category_label}</span>
                      ) : (
                        <span className="text-xs text-gray-400">—</span>
                      )}
                    </td>
                    <td className="py-2.5">
                      <span className={`text-xs px-2 py-0.5 rounded-full capitalize ${PRIORITY_TONES[s.priority] || PRIORITY_TONES.low}`}>
                        {s.priority}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default MonitoringReportTab;
